const STORAGE_PREFIX = 'equipment-selection:';
export const EQUIPMENT_SELECTION_DRAFT_TTL_MS = 10 * 60 * 1000;

export interface EquipmentSelectionDraft {
  /** Selected equipment IDs only. */
  ids: number[];
  /** Wizard step index (0-based). */
  step: number;
  /** Epoch ms of the last write. Draft expires 10 minutes after this. */
  savedAt: number;
}

const buildKey = (studentToken: string): string => (
  `${STORAGE_PREFIX}${studentToken.trim()}`
);

const parsePositiveInt = (value: unknown): number | null => {
  const n = typeof value === 'number' ? value : Number(value);
  if (!Number.isInteger(n) || n < 0) {
    return null;
  }
  return n;
};

const parseSavedAt = (value: unknown): number | null => {
  const n = typeof value === 'number' ? value : Number(value);
  if (!Number.isFinite(n) || n <= 0) {
    return null;
  }
  return n;
};

const isDraftExpired = (savedAt: number, now: number): boolean => (
  now - savedAt > EQUIPMENT_SELECTION_DRAFT_TTL_MS
);

/**
 * Reads a draft equipment selection from localStorage.
 * Persists only numeric IDs (+ step index), keyed by the public student token.
 * Expired drafts (older than 10 minutes) are removed and ignored.
 */
export const readEquipmentSelectionDraft = (
  studentToken: string,
  now: number = Date.now(),
): EquipmentSelectionDraft | null => {
  if (!studentToken.trim() || typeof window === 'undefined') {
    return null;
  }

  try {
    const storageKey = buildKey(studentToken);
    const raw = window.localStorage.getItem(storageKey);
    if (!raw) {
      return null;
    }

    const parsed = JSON.parse(raw) as Partial<EquipmentSelectionDraft>;
    const savedAt = parseSavedAt(parsed.savedAt);
    if (savedAt === null || isDraftExpired(savedAt, now)) {
      window.localStorage.removeItem(storageKey);
      return null;
    }

    const ids = Array.isArray(parsed.ids)
      ? parsed.ids
        .map(parsePositiveInt)
        .filter((id): id is number => id !== null && id > 0)
      : [];
    const step = parsePositiveInt(parsed.step) ?? 0;

    return { ids: Array.from(new Set(ids)), step, savedAt };
  } catch {
    return null;
  }
};

export const writeEquipmentSelectionDraft = (
  studentToken: string,
  draft: Omit<EquipmentSelectionDraft, 'savedAt'> & { savedAt?: number },
  now: number = Date.now(),
): void => {
  if (!studentToken.trim() || typeof window === 'undefined') {
    return;
  }

  try {
    const ids = Array.from(
      new Set(
        draft.ids
          .map(parsePositiveInt)
          .filter((id): id is number => id !== null && id > 0),
      ),
    );
    const step = Math.max(0, parsePositiveInt(draft.step) ?? 0);
    const savedAt = parseSavedAt(draft.savedAt) ?? now;

    window.localStorage.setItem(
      buildKey(studentToken),
      JSON.stringify({ ids, step, savedAt } satisfies EquipmentSelectionDraft),
    );
  } catch {
    // Ignore quota / private mode failures.
  }
};

export const clearEquipmentSelectionDraft = (studentToken: string): void => {
  if (!studentToken.trim() || typeof window === 'undefined') {
    return;
  }

  try {
    window.localStorage.removeItem(buildKey(studentToken));
  } catch {
    // Ignore storage failures.
  }
};
